import { doc, onSnapshot, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { CELEBRATION_VISIT_LIMIT, FALL_ACTIVITIES, FREE_SPACE, emptyBingoProgress, getCompletedLines, normalizeBingoProgress } from "./fallBingoData";

export const FALL_BINGO_DOCUMENT = "fall-bingo-2026";
export const bingoStorageKey = (userId) => `mcc:fall-bingo:2026:v3:${userId}`;

export function normalizeBingoDocument(saved, defaultName = "") {
  const savedName = typeof saved?.prizeName === "string" ? saved.prizeName.slice(0, 100) : "";
  // Older cards automatically used the account name. Leave that field blank
  // until the participant enters a name, while retaining their chosen names.
  const automaticName = !saved?.prizeNameCustomized && defaultName
    && savedName.trim().toLowerCase() === defaultName.trim().toLowerCase();
  return {
    ...normalizeBingoProgress(saved),
    schemaVersion: 3,
    prizeName: automaticName ? "" : savedName,
    prizeNameCustomized: saved?.prizeNameCustomized === true,
    prizeGender: saved?.prizeGender === "male" ? "male" : "female",
    revision: Number.isSafeInteger(saved?.revision) && saved.revision >= 0 ? saved.revision : 0,
    appliedActions: Array.isArray(saved?.appliedActions) ? saved.appliedActions.filter((id) => typeof id === "string").slice(-200) : [],
  };
}

// Each action is rebased onto the latest cloud document, rather than replacing it
// with a device's potentially stale card. IDs make retries/snapshot echoes safe.
export function applyBingoAction(current, action) {
  if (current.appliedActions.includes(action.id)) return current;
  let next = { ...current };
  switch (action.type) {
    case "visit":
      next.celebration = current.celebration && current.celebration.visits < CELEBRATION_VISIT_LIMIT
        ? { ...current.celebration, visits: current.celebration.visits + 1 } : null;
      break;
    case "mark": {
      if (!Number.isInteger(action.cell) || action.cell === FREE_SPACE || action.cell < 0 || action.cell >= FALL_ACTIVITIES.length) return current;
      const marked = action.marked ? [...new Set([...current.marked, action.cell])] : current.marked.filter((cell) => cell !== action.cell);
      const newLines = getCompletedLines(marked).filter((line) => !current.celebrated.includes(line.id));
      const firstFinish = marked.length === FALL_ACTIVITIES.length && !current.completedAt;
      next = {
        ...current, marked,
        celebrated: [...current.celebrated, ...newLines.map((line) => line.id)],
        completedAt: firstFinish ? action.at : current.completedAt,
        celebration: firstFinish || (action.marked && newLines.length)
          ? { id: firstFinish ? "complete" : newLines.map((line) => line.id).join(","), kind: firstFinish ? "complete" : "line", visits: 1, lineCount: firstFinish ? getCompletedLines(marked).length : newLines.length }
          : current.celebration,
      };
      break;
    }
    case "dismiss":
      // An older device closing a banner must not dismiss a newly earned line.
      if (current.celebration?.id === action.bannerId) next.celebration = null;
      break;
    case "reset":
      next = { ...current, ...emptyBingoProgress() };
      break;
    case "name":
      next.prizeName = action.value.slice(0, 100);
      next.prizeNameCustomized = true;
      break;
    case "gender":
      next.prizeGender = action.value === "male" ? "male" : "female";
      break;
    default:
      return current;
  }
  return { ...next, appliedActions: [...current.appliedActions, action.id].slice(-200) };
}

export const firestoreBingoStore = {
  subscribe(userId, onData, onError) {
    return onSnapshot(doc(db, "users", userId, "playground", FALL_BINGO_DOCUMENT), { includeMetadataChanges: true }, (snapshot) => {
      if (snapshot.exists()) onData(snapshot.data(), !snapshot.metadata.fromCache && !snapshot.metadata.hasPendingWrites);
    }, onError);
  },
  commit(userId, action, fallback, defaultName = "") {
    const reference = doc(db, "users", userId, "playground", FALL_BINGO_DOCUMENT);
    return runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(reference);
      // Import this account's browser card only when no cloud card exists.
      const current = snapshot.exists() ? normalizeBingoDocument(snapshot.data(), defaultName) : fallback;
      const next = applyBingoAction(current, action);
      if (snapshot.exists() && next === current) return current;
      const saved = { ...next, revision: current.revision + 1 };
      transaction.set(reference, { ...saved, updatedAt: serverTimestamp() });
      return saved;
    });
  },
};

const newActionId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

function readBingoCache(key) {
  try { return JSON.parse(window.localStorage.getItem(key)); } catch { return null; }
}

export function createBingoSession(userId, defaultName, visitId, store = firestoreBingoStore) {
  const key = bingoStorageKey(userId);
  const cached = readBingoCache(key);
  let base = normalizeBingoDocument(cached, defaultName);
  let pending = Array.isArray(cached?.pendingActions) ? cached.pendingActions.filter(isValidAction) : [];
  if (!pending.some((action) => action.id === visitId)) pending.push({ id: visitId, type: "visit" });
  let loading = true;
  let error = false;
  let cacheError = false;
  let running = false;
  let active = false;
  let unsubscribe;
  let listenerFailed = false;
  let retryTimer;
  let retryDelay = 1000;
  let notify = () => {};

  function snapshot() {
    return { progress: pending.reduce(applyBingoAction, base), loading, error, cacheError, saving: pending.length > 0 };
  }
  function publish() {
    const state = snapshot();
    try {
      const owner = readBingoCache(key)?.cacheOwner;
      // A save finishing after navigation must not overwrite a newer visit's cache.
      if (active || !owner || owner === visitId) {
        window.localStorage.setItem(key, JSON.stringify({ ...state.progress, pendingActions: pending, cacheOwner: visitId }));
      }
      cacheError = false;
    } catch { cacheError = true; }
    if (active) notify({ ...state, cacheError });
  }
  function listen() {
    unsubscribe?.();
    listenerFailed = false;
    unsubscribe = store.subscribe(userId, (data, fromServer) => {
      const incoming = normalizeBingoDocument(data, defaultName);
      if (incoming.revision >= base.revision) base = incoming;
      if (fromServer && !pending.length) error = false;
      publish();
    }, () => { listenerFailed = true; error = true; loading = false; publish(); });
  }
  async function flush() {
    if (running || !pending.length) return;
    running = true;
    error = false;
    publish();
    while (pending.length) {
      const action = pending[0];
      try {
        const saved = await store.commit(userId, action, base, defaultName);
        if (saved.revision >= base.revision) base = saved;
        pending = pending.filter((item) => item.id !== action.id);
        loading = false;
        retryDelay = 1000;
        publish();
      } catch (failure) {
        error = true;
        loading = false;
        publish();
        // Transactions require a connection. Keep actions in the browser and
        // retry transient failures; permission failures have a manual retry.
        if (active && ["unavailable", "aborted", "deadline-exceeded"].includes(failure.code)) {
          retryTimer = window.setTimeout(retry, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 30000);
        }
        break;
      }
    }
    running = false;
  }
  function retry() {
    window.clearTimeout(retryTimer);
    if (listenerFailed) listen();
    flush();
  }
  return {
    snapshot,
    start(onChange) {
      active = true;
      notify = onChange;
      listen();
      window.addEventListener("online", retry);
      flush();
      return () => {
        active = false;
        unsubscribe?.();
        window.removeEventListener("online", retry);
        window.clearTimeout(retryTimer);
      };
    },
    dispatch(action) {
      pending.push({ ...action, id: newActionId(), at: new Date().toISOString() });
      publish();
      if (!error) flush();
    },
    retry,
  };
}

function isValidAction(action) {
  if (!action || typeof action.id !== "string") return false;
  switch (action.type) {
    case "mark": return Number.isInteger(action.cell) && action.cell >= 0 && action.cell < FALL_ACTIVITIES.length && typeof action.marked === "boolean" && typeof action.at === "string" && Number.isFinite(Date.parse(action.at));
    case "name": return typeof action.value === "string";
    case "gender": return ["female", "male"].includes(action.value);
    case "dismiss": return typeof action.bannerId === "string";
    case "visit":
    case "reset": return true;
    default: return false;
  }
}

export const createBingoVisitId = newActionId;
